import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { getReportSummary } from '../lib/api';
import PageHeader from '../components/layout/PageHeader';

export default function Dashboard() {
  const navigate = useNavigate();
  const { data } = useQuery({ queryKey: ['report-summary'], queryFn: getReportSummary });

  const cards = [
    {
      to: '/inventory',
      label: 'Inventory',
      value: data?.total_items,
      icon: BoxIcon,
      color: 'bg-blue-50 text-brand-700',
    },
    {
      to: '/reports',
      label: 'Total Value',
      value: data?.total_value != null ? `$${data.total_value.toFixed(2)}` : null,
      icon: ChartIcon,
      color: 'bg-green-50 text-green-700',
    },
    {
      to: '/reports?tab=low-stock',
      label: 'Low Stock',
      value: data?.low_stock_count,
      icon: AlertIcon,
      color: 'bg-yellow-50 text-yellow-700',
    },
    {
      to: '/admin',
      label: 'Admin',
      icon: UsersIcon,
      color: 'bg-purple-50 text-purple-700',
    },
  ];

  return (
    <div className="h-full flex flex-col">
      <PageHeader
        title="Overview"
        breadcrumb={[{ label: 'Dashboard' }]}
      />
      <div className="flex-1 p-6">
        <div className="grid grid-cols-2 gap-4 max-w-3xl">
          {cards.map(({ to, label, value, icon: Icon, color }) => (
            <button
              key={to}
              onClick={() => navigate(to)}
              className="bg-white border border-gray-200 rounded-xl p-6 flex flex-col items-center justify-center gap-3
                         hover:border-brand-300 hover:shadow-sm transition-all text-center min-h-[160px]"
            >
              <div className={`w-14 h-14 rounded-full flex items-center justify-center ${color}`}>
                <Icon className="w-6 h-6" />
              </div>
              {value != null && (
                <p className="text-2xl font-bold text-gray-900">{value}</p>
              )}
              <p className="text-sm font-semibold text-gray-700">{label}</p>
            </button>
          ))}
        </div>

        {/* Recent activity */}
        {data?.recent_activity && data.recent_activity.length > 0 && (
          <div className="mt-8 max-w-3xl">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">Recent Activity</h2>
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <tbody className="divide-y divide-gray-100">
                  {data.recent_activity.map((t) => (
                    <tr key={t.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5">
                        <span className={`badge ${t.transaction_type === 'receive' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {t.transaction_type}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 font-medium text-gray-900">{t.item_name}</td>
                      <td className="px-4 py-2.5 text-gray-500">{t.part_number}</td>
                      <td className="px-4 py-2.5 text-right font-medium">
                        {t.transaction_type === 'issue' ? '-' : '+'}{t.quantity}
                      </td>
                      <td className="px-4 py-2.5 text-right text-gray-400">
                        {new Date(t.performed_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function BoxIcon({ className }: { className?: string }) {
  return <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
      d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
  </svg>;
}
function ChartIcon({ className }: { className?: string }) {
  return <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>;
}
function AlertIcon({ className }: { className?: string }) {
  return <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
  </svg>;
}
function UsersIcon({ className }: { className?: string }) {
  return <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>;
}
